import {useContext, useEffect} from 'react'

import PropTypes from 'prop-types'

import jsCookie from '@s-ui/js/lib/cookie'
import Context from '@s-ui/react-context'
import {useMount} from '@s-ui/react-hooks'
import {useLocation} from '@s-ui/react-router'
import {Messaging, useChannelActions, useMessagingThirdPartyContext} from '@adv-mt/mt-messaging'

import {useInteractionsConfig} from './config/useInteractionsConfig.js'
import {useMessagingContext} from './useMessagingContext/index.js'
import {useGetIntegrationActions} from './useGetIntegrationActions.js'
import {extractAdIdsFromChannels, mapAdsInfo} from './utils.js'

const BLOCKED_USERS_COOKIE_KEY = 'blocked_users'

const MessagingThirdParty = ({adsList, adsDetails, getAdDetailResponse, updateAdsList}) => {
  const {currentChannels, currentUser, currentBlockedUsers} = useMessagingThirdPartyContext()
  const {channelToShow, updateIntegrationButtonPropsMap, systemMessageLinks, setSystemMessageOnClickActions} =
    useChannelActions()

  const {domain} = useContext(Context)

  const {state: routerState, query} = useLocation()
  const {state, setInteractionInfo} = useMessagingContext()
  const {interactionComponent, showInteractionComponent} = state
  const {getForcedInteractionsConfig, getStateInteractionsConfig} = useInteractionsConfig(routerState)

  const {formatActionsByChannel, getLinkActionUrl, setInteractionResponse} = useGetIntegrationActions()

  const getAdsByIds = async adIds => {
    const [error, adsList] = await domain.get('get_messaging_ads_list_user_use_case').execute({adIds})

    if (error) console.error(error)
    return adsList || []
  }

  useMount(() => {
    const configObject = getStateInteractionsConfig(routerState)
    setInteractionResponse(configObject)
  })

  useMount(async () => {
    if (routerState?.adId) {
      const adsList = await getAdsByIds([routerState.adId])
      const ads = mapAdsInfo(adsList?.ads)
      updateAdsList(ads)
    }
  })

  useMount(() => {
    const {interaction} = query
    if (!interaction) return
    setInteractionInfo({params: query, url: location.href})
    const configObject = getForcedInteractionsConfig(query)
    setInteractionResponse(configObject)
  })

  useEffect(() => {
    const formattedActionsByChannel = formatActionsByChannel({currentChannelId: channelToShow?.id})
    updateIntegrationButtonPropsMap(formattedActionsByChannel)
  }, [currentChannels, currentUser?.id, channelToShow?.id])

  useEffect(() => {
    if (!systemMessageLinks) return

    const newOnClickMap = Object.fromEntries(
      Object.values(systemMessageLinks).map(({id, link}) => [id, {id, onClick: () => getLinkActionUrl(link)}])
    )

    setSystemMessageOnClickActions(newOnClickMap)
  }, [systemMessageLinks])

  useEffect(() => {
    const fetchAdsFromStreamChannels = async () => {
      if (Boolean(currentChannels) && currentChannels.error === false && currentChannels?.data.length) {
        const adIdsList = extractAdIdsFromChannels(currentChannels?.data)

        if (adIdsList.length > 0) {
          const adsList = await getAdsByIds(adIdsList)
          const ads = mapAdsInfo(adsList?.ads)
          updateAdsList(ads)
        }
      }
    }

    fetchAdsFromStreamChannels()
  }, [currentChannels])

  useEffect(() => {
    const currentChannelId = channelToShow?.data?.ad?.adId
    if (adsList?.[currentChannelId] && (channelToShow !== null || !!adsDetails?.[currentChannelId])) {
      getAdDetailResponse({adId: channelToShow?.data?.ad?.adId})
    }
  }, [channelToShow?.cid, adsList])

  useEffect(() => {
    const blockedUserIds = currentBlockedUsers.map(item => item.blocked_user_id)
    jsCookie.set(BLOCKED_USERS_COOKIE_KEY, JSON.stringify(blockedUserIds))
  }, [currentBlockedUsers])

  return (
    <>
      {showInteractionComponent && interactionComponent !== null && interactionComponent()}
      <Messaging />
    </>
  )
}

MessagingThirdParty.displayName = 'MessagingThirdParty'

MessagingThirdParty.propTypes = {
  adsList: PropTypes.object,
  adsDetails: PropTypes.object,
  getAdDetailResponse: PropTypes.func,
  updateAdsList: PropTypes.func
}

export default MessagingThirdParty
